import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  UsersIcon, 
  UserPlusIcon, 
  ClockIcon, 
  CalendarIcon, 
  CurrencyDollarIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  BriefcaseIcon,
  DocumentTextIcon,
  UserGroupIcon,
  CheckBadgeIcon,
  EyeIcon,
  ShieldCheckIcon,
  GiftIcon,
  FlagIcon
} from '@heroicons/react/24/outline';
import Sidebar from './Components/Sidebar';
import Header from './Components/Header';
import EmployeeList from './Components/EmployeeList';
import EmployeeForm from './Components/EmployeeForm';
import Attendance from './Components/Attendance';
import LeaveRequests from './Components/LeaveRequests';
import Payroll from './Components/Payroll';
import Reports from './Components/Reports';
import Settings from './Components/Settings';
import OfferLetterGenerator from './Components/OfferLetterGenerator';
import AddExpense from './Components/AddExpense';
import AddWork from './Components/AddWork';
import Recruitment from './Components/Recruitment';
import Performance from './Components/Performance';
import EmployeeManagement from './Components/EmployeeManagement';
import PolicyDocument from './Components/Privacy';
import AddWorkspace from './Components/AddWorkspace';

const Dashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [user, setUser] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showPDFViewer, setShowPDFViewer] = useState(false);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    
    if (!userData || !token) {
      navigate('/');
      return;
    }
    
    const parsedUser = JSON.parse(userData);
    console.log('User loaded:', parsedUser); // Debug log
    setUser(parsedUser);

    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) {
        setSidebarOpen(true);
      } else {
        setSidebarOpen(false);
      }
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [navigate]);

  const getUserRole = () => {
    return user?.role || user?.userRole || 'employee';
  };

  const handleViewProfile = (employee) => {
    setSelectedEmployee(employee);
    setShowProfileModal(true);
  };

  const renderContent = () => {
    const role = getUserRole();
    console.log('Current role:', role, 'Active tab:', activeTab); // Debug log
    
    switch(activeTab) {
      case 'dashboard':
        return <DashboardHome userRole={role} user={user} onViewProfile={handleViewProfile} showPDFViewer={showPDFViewer} setShowPDFViewer={setShowPDFViewer} />;
      
      case 'employeemanagement':
      case 'employees':
        if (['hr', 'admin', 'superadmin', 'employee'].includes(role)) {
          return <EmployeeManagement userRole={role} onViewProfile={handleViewProfile} />;
        }
        return <AccessDenied />;
      
      case 'add-employee':
        if (['hr', 'admin', 'superadmin', 'employee'].includes(role)) {
          return <EmployeeForm userRole={role} />;
        }
        return <AccessDenied />;
       case 'add-expense':
        if (['hr', 'admin', 'superadmin', 'employee'].includes(role)) {
          return <AddExpense userRole={role} />;
        }
         case 'recruitment':
        if (['hr', 'admin', 'superadmin', 'employee'].includes(role)) {
          return <Recruitment userRole={role} />;
        }
        return <AccessDenied />;
      case 'attendance':
        return <Attendance userRole={role} userId={user?.id} />;
        case 'add-workspace':
        return <AddWorkspace userRole={role} userId={user?.id} />;
      
      case 'leaves':
        return <LeaveRequests userRole={role} userId={user?.id} />;
      
      case 'payroll':
        if (['hr', 'admin', 'superadmin', 'employee'].includes(role)) {
          return <Payroll userRole={role} />;
        }
        return <Payroll userRole={role} userId={user?.id} />;
      
      case 'reports':
        if (['hr', 'admin', 'superadmin', 'employee'].includes(role)) {
          return <Reports userRole={role} />;
        }
        return <AccessDenied />;
      
      case 'settings':
        if (['superadmin' , 'hr', 'employee'].includes(role)) {
          return <Settings userRole={role} />;
        }
        return <AccessDenied />;
      
      case 'performance':
        return <Performance userRole={role} userId={user?.id} />;
      
      case 'offerletter':
        if (['hr', 'admin', 'superadmin', 'employee'].includes(role)) {
          return <OfferLetterGenerator />;
        }
        return <AccessDenied />;
      case 'policydocument':
        if (['hr', 'admin', 'superadmin', 'employee'].includes(role)) {
          return <PolicyDocument onClose={() => setActiveTab('dashboard')} />;
        }
        return <AccessDenied />;
      
      default:
        return <DashboardHome userRole={role} user={user} onViewProfile={handleViewProfile} showPDFViewer={showPDFViewer} setShowPDFViewer={setShowPDFViewer} />;
    }
  };
const handlePrivacyPolicy = () => {
  setShowPDFViewer(true);
};
  if (!user) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <div className="flex h-screen bg-gray-100 overflow-hidden">
        <Sidebar 
          activeTab={activeTab} 
          setActiveTab={setActiveTab}
          userRole={getUserRole()}
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          isMobile={isMobile}
          user={user}
        />
        
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header 
            user={user} 
            setSidebarOpen={setSidebarOpen}
            isMobile={isMobile}
          />
          <main className="flex-1 overflow-y-auto p-4 md:p-6 lg:p-8">
            {renderContent()}
          </main>
        </div>
      </div>

      {/* Profile Modal */}
      {showProfileModal && selectedEmployee && (
        <ProfileModal employee={selectedEmployee} onClose={() => setShowProfileModal(false)} />
      )}
    </>
  );
};

// Profile Modal Component
const ProfileModal = ({ employee, onClose }) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-800">Employee Profile</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-20 h-20 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 text-2xl font-bold">
              {employee.name?.charAt(0) || 'U'}
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-800">{employee.name}</h3>
              <p className="text-gray-500">{employee.position || 'Software Developer'}</p>
              <span className="inline-block mt-1 text-xs px-2 py-1 bg-indigo-100 text-indigo-700 rounded-full">
                {employee.role?.toUpperCase() || employee.userRole?.toUpperCase() || 'EMPLOYEE'}
              </span>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InfoField label="Email" value={employee.email || 'employee@aizops.com'} />
            <InfoField label="Phone" value={employee.phone || '+91 98765 43210'} />
            <InfoField label="Department" value={employee.department || 'Engineering'} />
            <InfoField label="Join Date" value={employee.joinDate || '2024-01-15'} />
            <InfoField label="Date of Birth" value={employee.dob || '1995-05-15'} />
            <InfoField label="Employee ID" value={employee.id || 'EMP001'} />
            <InfoField label="Role" value={employee.role || employee.userRole || 'Employee'} />
          </div>
        </div>
      </div>
    </div>
  );
};

const InfoField = ({ label, value }) => (
  <div>
    <label className="text-xs text-gray-500">{label}</label>
    <p className="text-sm font-medium text-gray-800">{value}</p>
  </div>
);

// Loading Spinner Component
const LoadingSpinner = () => (
  <div className="flex items-center justify-center h-screen bg-gray-100">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600 mx-auto"></div>
      <p className="mt-4 text-gray-600">Loading dashboard...</p>
    </div>
  </div>
);

// Access Denied Component
const AccessDenied = () => (
  <div className="flex flex-col items-center justify-center py-20">
    <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mb-4">
      <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m0 0v2m0-2h2m-2 0H9m3-6V8m0 0V6m0 2h2m-2 0H9M5 3h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2z" />
      </svg>
    </div>
    <h2 className="text-xl font-semibold text-gray-800 mb-2">Access Denied</h2>
    <p className="text-gray-500">You don't have permission to view this page.</p>
  </div>
);

// Dashboard Home Component
const DashboardHome = ({ userRole, user, onViewProfile, showPDFViewer, setShowPDFViewer }) => {
  // Employees data with birthdays
  const [employees] = useState([
    { id: 1, name: "Harsh Yadav", email: "harsh@aizops.com", role: "employee", userRole: "employee", department: "Engineering", dob: "1995-03-15", position: "Senior Developer", phone: "+91 98765 43210", joinDate: "2023-01-15" },
    { id: 2, name: "Aman Mahato", email: "aman@aizops.com", role: "manager", userRole: "manager", department: "IT", dob: "1992-07-22", position: "IT Manager", phone: "+91 98765 43211", joinDate: "2022-06-10" },
    { id: 3, name: "Saurav Kumar", email: "saurav@aizops.com", role: "employee", userRole: "employee", department: "Marketing", dob: "1996-11-08", position: "Marketing Executive", phone: "+91 98765 43212", joinDate: "2023-08-20" },
    { id: 4, name: "Sanjiv Kumar", email: "sanjiv@aizops.com", role: "hr", userRole: "hr", department: "HR", dob: "1990-12-25", position: "HR Manager", phone: "+91 98765 43213", joinDate: "2021-03-05" },
  ]);

  // Government Holidays
  const governmentHolidays = [
    { id: 1, name: "Republic Day", date: "2024-01-26", day: "Friday", type: "National" },
    { id: 2, name: "Holi", date: "2024-03-25", day: "Monday", type: "Festival" },
    { id: 3, name: "Independence Day", date: "2024-08-15", day: "Thursday", type: "National" },
    { id: 4, name: "Gandhi Jayanti", date: "2024-10-02", day: "Wednesday", type: "National" },
    { id: 5, name: "Diwali", date: "2024-11-12", day: "Tuesday", type: "Festival" },
    { id: 6, name: "Christmas", date: "2024-12-25", day: "Wednesday", type: "Festival" },
  ];

  // Calculate upcoming birthdays
  const getUpcomingBirthdays = () => {
    const today = new Date();
    const currentYear = today.getFullYear();
    
    return employees
      .map(emp => {
        const dob = new Date(emp.dob);
        const birthdayThisYear = new Date(currentYear, dob.getMonth(), dob.getDate());
        const daysUntil = Math.ceil((birthdayThisYear - today) / (1000 * 60 * 60 * 24));
        
        return {
          ...emp,
          birthdayDate: birthdayThisYear,
          daysUntil: daysUntil >= 0 ? daysUntil : daysUntil + 365,
          dateStr: birthdayThisYear.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        };
      })
      .filter(emp => emp.daysUntil <= 30 && emp.daysUntil >= 0)
      .sort((a, b) => a.daysUntil - b.daysUntil);
  };

  // Get upcoming holidays
  const getUpcomingHolidays = () => {
    const today = new Date();
    return governmentHolidays
      .filter(holiday => new Date(holiday.date) >= today)
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .slice(0, 5);
  };

  const stats = [
    { title: "Total Employees", value: employees.length.toString(), icon: UsersIcon, color: "bg-blue-500", change: "+12%", changeColor: "text-green-600", allowedRoles: ['hr', 'admin', 'superadmin'] },
    { title: "Present Today", value: "142", icon: UserGroupIcon, color: "bg-green-500", change: "+8%", changeColor: "text-green-600", allowedRoles: ['hr', 'admin', 'superadmin', 'manager'] },
    { title: "On Leave", value: "8", icon: BriefcaseIcon, color: "bg-yellow-500", change: "-2%", changeColor: "text-red-600", allowedRoles: ['hr', 'admin', 'superadmin', 'manager'] },
    { title: "Pending Requests", value: "12", icon: DocumentTextIcon, color: "bg-purple-500", change: "+5%", changeColor: "text-orange-600", allowedRoles: ['hr', 'admin', 'superadmin', 'manager'] },
  ];

  const upcomingBirthdays = getUpcomingBirthdays();
  const upcomingHolidays = getUpcomingHolidays();



  const getRoleDisplayName = (role) => {
    switch(role) {
      case 'superadmin': return 'Super Admin';
      case 'admin': return 'Admin';
      case 'hr': return 'HR Manager';
      case 'manager': return 'Manager';
      default: return 'Employee';
    }
  };

  const getRoleColor = (role) => {
    switch(role) {
      case 'superadmin': return 'bg-red-100 text-red-700';
      case 'admin': return 'bg-purple-100 text-purple-700';
      case 'hr': return 'bg-blue-100 text-blue-700';
      case 'manager': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="space-y-6">
      {/* Welcome Banner with Buttons */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 md:p-8 shadow-sm">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">
              Welcome back, {user?.name?.split(' ')[0] || 'User'}!
            </h1>
            <p className="text-gray-500 text-sm md:text-base">
              Here's what's happening with your workforce today.
            </p>
            <div className="mt-4 inline-flex items-center gap-2 bg-gray-100 rounded-lg px-4 py-2">
              <CheckBadgeIcon className="w-5 h-5 text-indigo-600" />
              <span className={`text-sm font-medium px-2 py-1 rounded ${getRoleColor(userRole)}`}>
                Role: {getRoleDisplayName(userRole)}
              </span>
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => onViewProfile(user)}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
            >
              <EyeIcon className="w-4 h-4" />
              View Profile
            </button>
            <button
  className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
>
  <ShieldCheckIcon className="w-4 h-4" />
  Privacy Policy
</button>
          
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        {stats.map((stat, index) => {
          if (!stat.allowedRoles.includes(userRole)) return null;
          
          return (
            <div key={index} className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className={`${stat.color} p-3 rounded-xl text-white`}>
                    <stat.icon className="w-6 h-6" />
                  </div>
                  <span className={`text-sm font-semibold ${stat.changeColor}`}>{stat.change}</span>
                </div>
                <h3 className="text-2xl font-bold text-gray-800">{stat.value}</h3>
                <p className="text-gray-500 text-sm mt-1">{stat.title}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Three Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Attendance Chart */}
        <div className="lg:col-span-1 bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-800">Attendance Overview</h2>
              <p className="text-sm text-gray-500">This week's statistics</p>
            </div>
          </div>
          <div className="space-y-4">
            {[
              { day: "Monday", present: 85 },
              { day: "Tuesday", present: 88 },
              { day: "Wednesday", present: 92 },
              { day: "Thursday", present: 90 },
              { day: "Friday", present: 87 },
            ].map((day, idx) => (
              <div key={idx} className="space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="font-medium text-gray-600">{day.day}</span>
                  <span className="text-gray-500">{day.present}%</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-green-500 rounded-full transition-all duration-500"
                    style={{ width: `${day.present}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Upcoming Birthdays */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center gap-2 mb-4">
            <GiftIcon className="w-5 h-5 text-pink-500" />
            <h2 className="text-lg font-semibold text-gray-800">Upcoming Birthdays</h2>
          </div>
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {upcomingBirthdays.length > 0 ? (
              upcomingBirthdays.map(emp => (
                <div key={emp.id} className="flex items-center gap-3 p-3 bg-gradient-to-r from-pink-50 to-purple-50 rounded-lg cursor-pointer hover:shadow-md transition" onClick={() => onViewProfile(emp)}>
                  <div className="w-10 h-10 bg-gradient-to-r from-pink-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold">
                    {emp.name.charAt(0)}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-800">{emp.name}</p>
                    <p className="text-xs text-gray-500">{emp.position}</p>
                  </div>
                  <div className="text-right">
                    <span className="text-xs font-medium text-pink-600 bg-pink-100 px-2 py-1 rounded-full">
                      {emp.daysUntil === 0 ? 'Today!' : `${emp.daysUntil} days`}
                    </span>
                    <p className="text-xs text-gray-400 mt-1">{emp.dateStr}</p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-center py-4">No upcoming birthdays</p>
            )}
          </div>
        </div>

        {/* Government Holidays */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center gap-2 mb-4">
            <FlagIcon className="w-5 h-5 text-red-500" />
            <h2 className="text-lg font-semibold text-gray-800">Government Holidays</h2>
          </div>
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {upcomingHolidays.map(holiday => (
              <div key={holiday.id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-800">{holiday.name}</p>
                  <p className="text-xs text-gray-500">{holiday.type}</p>
                </div>
                <div className="text-right">
                  <span className="text-xs font-medium text-red-600 bg-red-100 px-2 py-1 rounded-full">
                    {holiday.day}
                  </span>
                  <p className="text-xs text-gray-400 mt-1">{new Date(holiday.date).toLocaleDateString()}</p>
                </div>
              </div>
            ))}
          </div>
          <button className="w-full mt-4 text-center text-sm text-indigo-600 hover:text-indigo-700 font-medium">
            View All Holidays →
          </button>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-800">Recent Activity</h2>
        </div>
        <div className="divide-y divide-gray-100">
          <div className="px-6 py-4 hover:bg-gray-50 transition-colors">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-yellow-500 rounded-full flex items-center justify-center text-white font-bold">H</div>
              <div className="flex-1">
                <p className="text-sm text-gray-800"><span className="font-semibold">Harsh Sharma</span> requested leave</p>
                <p className="text-xs text-gray-400">2 hours ago</p>
              </div>
              <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full">Pending</span>
            </div>
          </div>
          <div className="px-6 py-4 hover:bg-gray-50 transition-colors">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white font-bold">A</div>
              <div className="flex-1">
                <p className="text-sm text-gray-800"><span className="font-semibold">Aman Verma</span> joined the company</p>
                <p className="text-xs text-gray-400">5 hours ago</p>
              </div>
              <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">New Join</span>
            </div>
          </div>
          <div className="px-6 py-4 hover:bg-gray-50 transition-colors">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold">S</div>
              <div className="flex-1">
                <p className="text-sm text-gray-800"><span className="font-semibold">Saurav Kumar</span> marked attendance</p>
                <p className="text-xs text-gray-400">8 hours ago</p>
              </div>
              <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">Present</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;